import type { BackendTypes } from '@tf/api';
import { createStyles, Group, Icon, lighten, Stack, TFNotifier, TFText } from '@tf/ui';
import type React from 'react';

import type { DocumentCheck } from '../../../../../../api/documentCheck';
import { useCreateDocumentCheckMutation, useDocumentCheck } from '../../../../../../api/documentCheck';
import type { FileInfo } from '../../../../../../api/files';
import { idenfyErrorMessages } from '../../../../../../utils';

import { DocumentsCheckStatus } from './DocumentsCheckStatus';
import { StartCheckButton } from './StartCheckButton';

const useStyles = createStyles(({ colors, radius }) => ({
	errorBlockWrapper: {
		cursor: 'default',
		backgroundColor: lighten(colors.red[1], 0.6),
		color: colors.red[6],
		display: 'flex',
		alignItems: 'center',
		borderRadius: radius.sm,
		padding: '.75rem .75rem',
	},
}));

interface Props {
	files: FileInfo[];
	isReadOnly: boolean;
	documentCheckId?: string;
	onCheckCreated: (documentCheckId: string) => void;
}

export const DocumentsCheckComponent: React.FC<Props> = ({ files, isReadOnly, documentCheckId, onCheckCreated }) => {
	const { classes } = useStyles();

	const documentCheck = useDocumentCheck(documentCheckId);

	const createJobMutation = useCreateDocumentCheckMutation();

	const checkDocument = async (checkParams: BackendTypes.DocumentCheckParams) => {
		const checkId = await createJobMutation.mutateAsync(checkParams).catch(async (error: any) => {
			const defaultMessage = 'An unexpected error occurred. Please try again later.';
			try {
				const motorError: BackendTypes.MotorError = await error.json();
				const errorMessage =
					motorError.message || (motorError?.code && idenfyErrorMessages[motorError.code]) || defaultMessage;
				TFNotifier.error(errorMessage);
			} catch (_e) {
				TFNotifier.error(defaultMessage);
			}
		});

		if (checkId) {
			onCheckCreated(checkId);
		}
	};

	const isCheckedFileNotExist = (job: DocumentCheck | undefined) => {
		if (!job?.data?.frontSide.record_attachment._object) {
			return false;
		}
		return files.findIndex((file) => file.id === job?.data?.frontSide.record_attachment._object) < 0;
	};

	return (
		<Stack gap="md">
			{isCheckedFileNotExist(documentCheck.data) ? (
				<Group gap={6} className={classes.errorBlockWrapper}>
					<Icon.IconAlertCircle size={14} />
					<TFText size="sm" lineClamp={1}>
						Document check results are outdated.
						{!isReadOnly ? '' : 'Please check the new document'}
					</TFText>
				</Group>
			) : null}
			{documentCheck.data ? <DocumentsCheckStatus check={documentCheck.data} isReadOnly={isReadOnly} /> : null}
			{!isReadOnly ? (
				<StartCheckButton files={files} hasPreviousCheck={!!documentCheckId} onCheckDocument={checkDocument} />
			) : null}
		</Stack>
	);
};
